import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as ticketsApi from "./api";
import type { TicketFilters, TicketPriority } from "./api";

const KEY = "tickets";

export function useTickets(filters: TicketFilters) {
  return useQuery({
    queryKey: [KEY, filters],
    queryFn: () => ticketsApi.listTickets(filters),
    placeholderData: (prev) => prev,
  });
}

export function useTicket(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => ticketsApi.getTicket(id as string),
    enabled: id !== null,
  });
}

export function useTicketCategories() {
  return useQuery({
    queryKey: ["ticket-categories"],
    queryFn: ticketsApi.listCategories,
    staleTime: 5 * 60_000,
  });
}

export function useTicketCounts() {
  return useQuery({ queryKey: [KEY, "counts"], queryFn: ticketsApi.ticketCounts });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: [KEY] });
}

export function useCreateTicket() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: ticketsApi.createTicket, onSuccess: invalidate });
}

export function usePerformAction() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof ticketsApi.performAction>[1];
    }) => ticketsApi.performAction(id, body),
    onSuccess: invalidate,
  });
}

export function useDeleteTicket() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: ticketsApi.deleteTicket, onSuccess: invalidate });
}

export function useBulkTicketAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ticketsApi.bulkTicketAction,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      void qc.invalidateQueries({ queryKey: ["ticket-tags"] });
    },
  });
}

export function useChangePriority() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: TicketPriority }) =>
      ticketsApi.changePriority(id, priority),
    onSuccess: invalidate,
  });
}

export function useSetCallType() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      id,
      call_type,
      amount,
      remark,
    }: {
      id: string;
      call_type: ticketsApi.CallType;
      amount?: string | null;
      remark?: string | null;
    }) =>
      ticketsApi.setCallType(id, {
        call_type,
        ...(amount === undefined ? {} : { amount }),
        ...(remark === undefined ? {} : { remark }),
      }),
    onSuccess: invalidate,
  });
}

export function useMergeTickets() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ target_id, source_ids }: { target_id: string; source_ids: string[] }) =>
      ticketsApi.mergeTickets(target_id, source_ids),
    onSuccess: invalidate,
  });
}

export function useTransferSite() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { site_id: string; assignee_id?: string | null; reason?: string | null };
    }) => ticketsApi.transferSite(id, body),
    onSuccess: invalidate,
  });
}

export function useTags() {
  return useQuery({
    queryKey: ["ticket-tags"],
    queryFn: ticketsApi.listTags,
    staleTime: 60_000,
  });
}

export function useSetTicketTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tags }: { id: string; tags: string[] }) =>
      ticketsApi.setTicketTags(id, tags),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      void qc.invalidateQueries({ queryKey: ["ticket-tags"] });
    },
  });
}

export function useAddNote() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof ticketsApi.addNote>[1] }) =>
      ticketsApi.addNote(id, body),
    onSuccess: invalidate,
  });
}

export function useUploadAttachment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      ticketsApi.uploadAttachment(id, file),
    onSuccess: invalidate,
  });
}

export function useDeleteAttachment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (attachmentId: string) => ticketsApi.deleteAttachment(attachmentId),
    onSuccess: invalidate,
  });
}
